import * as THREE from 'three';
import type { SketchGuide, SketchLooseEdge, SketchSolid, SketchTag } from './types';
import { solidEffectiveVisible } from './types';
import { buildTopology, solidVertices } from './topology';

/**
 * Inference engine — Sketch's signature cursor intelligence (ported from
 * IngeTrazo's snap.py concepts, re-implemented in TypeScript).
 *
 * Priority ladder per pointer ray:
 *   FORCED LOCK first (arrow keys, or the momentary Shift hold the tool
 *   env feeds in as a lock): explicit direction beats everything — the
 *   endpoint rides the locked 3D axis line through the last committed
 *   point (resolved against the pointer ray, so vertical Z strokes work
 *   off the ground plane), capturing only exact geometry lying ON the
 *   line and alignment lines crossing it.
 *   Otherwise: endpoint → guide point → midpoint → on-edge → center,
 * then Tape Measure guide lines (the cursor rides the infinite line),
 * then live AXIS inference, Sketch-style: with a committed anchor point the
 * cursor's SCREEN heading from that anchor is compared against the screen
 * directions of the world X/Y/Z axes and the previous segment — the best
 * angular match (the "on axis" state) wins, and the endpoint is the point
 * on that 3D axis line closest to the pointer ray. Because the endpoint
 * rides the axis line (not the drawing plane) vertical Z strokes work off
 * the ground plane: mouse up = line up, in perspective and ortho alike.
 * Then FROM-POINT ALIGNMENT inference: the cursor's heading from ANY
 * geometry point (vertex / midpoint / center — not just the last commit)
 * is matched the same way; the point rides that 3D alignment line and the
 * classic dotted guide is drawn through the reference point. Two matches
 * on different axes CONVERGE to their intersection corner, and edge
 * extensions engage past either endpoint — the full Sketch linear
 * inference language.
 */

export type InferenceKind =
    | 'endpoint'
    | 'midpoint'
    | 'onEdge'
    | 'center'
    | 'onFace'
    | 'onGround'
    | 'onAxis'
    | 'guide';

export interface InferenceCandidate {
    point: THREE.Vector3;
    kind: InferenceKind;
    /** Screen-space distance (px) at which the candidate won. */
    priority: number;
}

export type AxisLock = 'x' | 'y' | 'z' | 'lastSegment' | null;

/** Identity of the axis behind an 'onAxis' result — drives the Sketch
 *  axis-color feedback (red X / green Y / blue Z, magenta lastSegment). */
export type InferredAxis = Exclude<AxisLock, null>;

/**
 * Per-type object-snap switches, shared with the app-wide snap store
 * (status bar icons / Snaps dialog). Sketch inference kinds map onto the
 * OSNAP families: endpoint → 'endpoint', midpoint → 'midpoint',
 * onEdge → 'nearest', center → 'center'. Absent keys stay active, so the
 * plain Sketch behavior is unchanged when no toggles are supplied.
 */
export interface SketchSnapToggles {
    endpoint?: boolean;
    midpoint?: boolean;
    center?: boolean;
    nearest?: boolean;
}

/** Master-off constant: every object-snap family disabled. */
export const SKETCH_SNAPS_OFF: SketchSnapToggles = {
    endpoint: false,
    midpoint: false,
    center: false,
    nearest: false,
};

export interface InferenceContext {
    solids: Record<string, SketchSolid>;
    guides: Record<string, SketchGuide>;
    camera: THREE.Camera;
    domElement: HTMLCanvasElement;
    /** Size in pixels of the snap aperture (screen distance). */
    aperturePx?: number;
}

const AXIS_DIRS = {
    x: new THREE.Vector3(1, 0, 0),
    y: new THREE.Vector3(0, 1, 0),
    z: new THREE.Vector3(0, 0, 1),
} as const;

/** Angular grab tolerance for live axis inference (Sketch-like ~15°). */
const SKETCH_AXIS_TOLERANCE_RAD = THREE.MathUtils.degToRad(15);
/** Cursor must clear the anchor (px) before a screen heading is meaningful. */
const MIN_HEADING_PX = 8;
/** Loose angular pre-filter for from-point alignment candidates: sampled
 *  screen directions vary with position in perspective, so the cheap
 *  reference test runs wider than the grab tolerance before the exact
 *  per-anchor sampling kicks in. */
const ALIGN_PREFILTER_RAD = THREE.MathUtils.degToRad(24);
/** An edge-extension grab must resolve beyond the segment ends by this
 *  fraction of the edge length — inside the span belongs to on-edge. */
const EXTENSION_CLEARANCE = 0.02;
/** A convergent corner (two alignment lines meeting) captures the cursor
 *  when its screen distance is within this multiple of the aperture. */
const CONVERGENCE_APERTURE_FACTOR = 2;
/** Screen corridor around a from-point alignment line (× aperture): the
 *  cursor must stay this close to the TRUE line regardless of anchor
 *  distance — pure angular matching would let a distant anchor grab the
 *  cursor far off the alignment it is supposed to follow. */
const ALIGN_CORRIDOR_APERTURE_FACTOR = 2;

/** Screen-space helper: project a world point to NDC px. */
function toScreen(p: THREE.Vector3, camera: THREE.Camera, domElement: HTMLCanvasElement): THREE.Vector2 {
    const rect = domElement.getBoundingClientRect();
    const ndc = p.clone().project(camera);
    return new THREE.Vector2(
        ((ndc.x + 1) / 2) * rect.width,
        ((1 - ndc.y) / 2) * rect.height
    );
}

/** World-space size of one screen pixel at `at`'s depth (perspective) or
 *  across the ortho frustum — screen-sized tolerances for world-space
 *  decisions. Local mirror of the tools-layer screenWorldTolerance, which
 *  core cannot import. */
function worldPerPx(camera: THREE.Camera, domElement: HTMLCanvasElement, at: THREE.Vector3): number {
    const height = domElement.clientHeight || domElement.height || 1;
    const persp = camera as THREE.PerspectiveCamera;
    if (persp.isPerspectiveCamera) {
        const fov = THREE.MathUtils.degToRad(persp.fov);
        return (2 * Math.tan(fov / 2) * persp.position.distanceTo(at)) / height;
    }
    const ortho = camera as THREE.OrthographicCamera;
    const zoom = ortho.zoom > 0 ? ortho.zoom : 1;
    return (ortho.top - ortho.bottom) / (height * zoom);
}

/**
 * Sketch direction grab: which world axis (or the previous segment's
 * direction) does the cursor's screen-space heading from `last` match?
 * Purely angular, so it behaves identically at any zoom and in perspective
 * and orthographic cameras. Returns null when the heading is ambiguous —
 * too close to the anchor, or no candidate within `toleranceRad`.
 */
export function inferScreenAxis(
    camera: THREE.Camera,
    domElement: HTMLCanvasElement,
    pointerPx: THREE.Vector2,
    last: THREE.Vector3,
    recent: THREE.Vector3[],
    toleranceRad: number = SKETCH_AXIS_TOLERANCE_RAD
): InferredAxis | null {
    const anchor = toScreen(last, camera, domElement);
    const heading = pointerPx.clone().sub(anchor);
    if (heading.length() < MIN_HEADING_PX) return null;
    heading.normalize();

    // Screen directions are sampled a small camera-relative step along each
    // candidate: stable in perspective, exact in ortho.
    const step = Math.max(1e-3, camera.position.distanceTo(last) * 0.05);
    const candidates: Array<{ id: InferredAxis; dir: THREE.Vector3 }> = [
        { id: 'x', dir: AXIS_DIRS.x },
        { id: 'y', dir: AXIS_DIRS.y },
        { id: 'z', dir: AXIS_DIRS.z },
    ];
    if (recent.length >= 2) {
        const segDir = recent[recent.length - 1].clone().sub(recent[recent.length - 2]);
        if (segDir.lengthSq() > 1e-12) candidates.push({ id: 'lastSegment', dir: segDir.normalize() });
    }

    let best: { id: InferredAxis; angle: number } | null = null;
    for (const { id, dir } of candidates) {
        const tip = toScreen(last.clone().addScaledVector(dir, step), camera, domElement);
        const ds = tip.clone().sub(anchor);
        // Axis pointing at the camera (top view for Z): not visually
        // inferable, skip it instead of snapping on noise.
        if (ds.length() < 2) continue;
        ds.normalize();
        // Axis lines extend both ways: compare headings mod 180°.
        const angle = Math.acos(THREE.MathUtils.clamp(Math.abs(heading.dot(ds)), -1, 1));
        if (angle <= toleranceRad && (!best || angle < best.angle)) {
            best = { id, angle };
        }
    }
    return best?.id ?? null;
}

/**
 * Camera ray through a canvas-LOCAL px position (same space as toScreen).
 * Returns null for degenerate canvas rects.
 */
function pointerRay(pointerPx: THREE.Vector2, camera: THREE.Camera, domElement: HTMLCanvasElement): THREE.Ray | null {
    const rect = domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const ndc = new THREE.Vector2(
        (pointerPx.x / rect.width) * 2 - 1,
        -(pointerPx.y / rect.height) * 2 + 1
    );
    camera.updateMatrixWorld();
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, camera);
    return raycaster.ray.clone();
}

/**
 * World point on the axis line (origin + s*dir) closest to the camera ray —
 * the point that appears under the cursor (Sketch's endpoint model). Falls
 * back to `fallback` when the ray runs (nearly) parallel to the axis —
 * looking straight down it, e.g. top view for Z — or the closest approach
 * sits behind the camera.
 */
function closestAxisPointToRay(
    ray: THREE.Ray,
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    fallback: THREE.Vector3
): THREE.Vector3 {
    const u = dir.clone().normalize();
    const d = ray.direction.clone().normalize();
    const D = u.dot(d);
    const denom = 1 - D * D;
    if (Math.abs(denom) < 1e-6) return fallback;
    const w0 = origin.clone().sub(ray.origin);
    const E = w0.dot(d);
    const F = w0.dot(u);
    const s = (D * E - F) / denom;
    const t = E + s * D;
    // Near-parallel rays make |s| explode (a sliver off parallel maps the
    // cursor to absurd distances): cap the resolved point to a sane
    // neighborhood of the anchor, else ride the plane projection.
    const maxDist = ray.origin.distanceTo(origin) * 4;
    if (!Number.isFinite(s) || t <= 0 || Math.abs(s) > maxDist) return fallback;
    return origin.clone().addScaledVector(u, s);
}

/** Infinite axis line through `origin` for the on-axis preview, spanned
 *  relative to the camera distance so it reads as endless at any zoom. */
function axisSpan(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    camera: THREE.Camera
): { a: THREE.Vector3; b: THREE.Vector3 } {
    const len = Math.max(1, camera.position.distanceTo(origin) * 1.5);
    const d = dir.clone().normalize();
    return {
        a: origin.clone().addScaledVector(d, -len),
        b: origin.clone().addScaledVector(d, len),
    };
}

export interface InferenceResult {
    point: THREE.Vector3;
    kind: InferenceKind;
    /** Axis line through `point` drawn while on-axis inference is live. */
    axisLine?: { a: THREE.Vector3; b: THREE.Vector3 };
    lockedAxis: AxisLock;
    /** Which axis an 'onAxis' result rides (color feedback / lock state). */
    axis?: InferredAxis;
    /** The model edge under the cursor when kind === 'onEdge' (guides use it). */
    edge?: { a: THREE.Vector3; b: THREE.Vector3 };
    /** Dotted alignment guides drawn through the reference point(s) when
     *  from-point alignment inference engages (kind === 'guide'). Each line
     *  carries its own axis so the two guides of a convergent corner take
     *  their own axis colors. */
    guideLines?: InferenceGuideLine[];
}

/** One dotted alignment guide of a 'guide' inference result. */
export interface InferenceGuideLine {
    a: THREE.Vector3;
    b: THREE.Vector3;
    /** Axis identity for color feedback; absent → neutral guide color. */
    axis?: InferredAxis;
}

export interface GeometryPools {
    vertices: THREE.Vector3[];
    edgeMidpoints: THREE.Vector3[];
    edges: Array<{ a: THREE.Vector3; b: THREE.Vector3 }>;
    centers: THREE.Vector3[];
}

/** Collect the snap pools from the current document (cheap enough at doc scale).
 *  Loose edges participate in the same ladder (endpoint/midpoint/on-edge). */
export function geometryPools(
    solids: Record<string, SketchSolid>,
    looseEdges: Record<string, SketchLooseEdge> = {},
    tags: Record<string, SketchTag> = {}
): GeometryPools {
    const vertices: THREE.Vector3[] = [];
    const edgeMidpoints: THREE.Vector3[] = [];
    const edges: Array<{ a: THREE.Vector3; b: THREE.Vector3 }> = [];
    const centers: THREE.Vector3[] = [];
    const seen = new Set<string>();

    for (const solid of Object.values(solids)) {
        if (!solidEffectiveVisible(solid, tags)) continue;
        for (const v of solidVertices(solid)) {
            const k = `${v.x.toFixed(4)}|${v.y.toFixed(4)}|${v.z.toFixed(4)}`;
            if (!seen.has(k)) {
                seen.add(k);
                vertices.push(v);
            }
        }
        const topo = buildTopology(solid);
        topo.edges.forEach((edge) => {
            edges.push({ a: edge.a, b: edge.b });
            edgeMidpoints.push(edge.a.clone().add(edge.b).multiplyScalar(0.5));
        });
        // Exact curve centers (Circle/Arc/Polygon tools store loopMeta).
        const seenCenters = new Set<string>();
        for (const face of solid.faces) {
            const meta = face.loopMeta;
            if (!meta) continue;
            const c = new THREE.Vector3(...meta.center);
            const k = `${c.x.toFixed(4)}|${c.y.toFixed(4)}|${c.z.toFixed(4)}`;
            if (!seenCenters.has(k)) {
                seenCenters.add(k);
                centers.push(c);
            }
        }
    }
    // Loose edges: endpoints + midpoints join the vertex/midpoint pools,
    // segments join the on-edge pool (Sketch draws snap across strokes).
    for (const edge of Object.values(looseEdges)) {
        const a = new THREE.Vector3(...edge.a);
        const b = new THREE.Vector3(...edge.b);
        for (const p of [a, b]) {
            const k = `${p.x.toFixed(4)}|${p.y.toFixed(4)}|${p.z.toFixed(4)}`;
            if (!seen.has(k)) {
                seen.add(k);
                vertices.push(p);
            }
        }
        edges.push({ a, b });
        edgeMidpoints.push(a.clone().add(b).multiplyScalar(0.5));
    }
    return { vertices, edgeMidpoints, edges, centers };
}

export interface InferenceInput {
    /** Raw cursor point on the active drawing plane (face plane or ground). */
    planePoint: THREE.Vector3;
    pools: GeometryPools;
    context: InferenceContext;
    /** Recent committed points (axis inference + lastSegment direction). */
    recent: THREE.Vector3[];
    /** Arrow-key / sticky axis lock from the active tool. */
    axisLock: AxisLock;
    pointerPx: THREE.Vector2;
    /** Per-type OSNAP switches (app-wide snap store). Omitted = all on. */
    snapTypes?: SketchSnapToggles;
    /** Tape Measure guide snapping (guide points + riding guide lines).
     *  Gated by the app-wide snap master switch; omitted = on. */
    guideSnapEnabled?: boolean;
    /** Alt-held modifier: the orthogonal axis grab (4) stands down for this
     *  hit, leaving object snaps (2) and the forced lock (1) live. */
    axisSnapSuppressed?: boolean;
}

/**
 * Resolve the best candidate for the current cursor. Pure function; tools
 * call it on every pointer-move.
 */
export function resolveInference(input: InferenceInput): InferenceResult {
    const { planePoint, pools, context, recent, axisLock, pointerPx, snapTypes, axisSnapSuppressed, guideSnapEnabled } = input;
    const aperture = context.aperturePx ?? 12;
    const snapOn = (t: keyof SketchSnapToggles): boolean => snapTypes?.[t] !== false;
    const guideSnap = guideSnapEnabled !== false;

    const screenOf = (p: THREE.Vector3) => toScreen(p, context.camera, context.domElement);

    // 1) Forced lock (arrow keys / the Shift hold tools feed in as a lock):
    // explicit direction beats everything — the object-snap ladder stands
    // down and the endpoint rides the locked 3D axis line through the last
    // committed point (resolved against the pointer ray, so a Z lock draws
    // vertically even off the ground plane). Along the way it still
    // captures exact geometry lying ON the line and alignment lines
    // (from-point axes, edge extensions) crossing it: the point slides to
    // the crossing while the direction itself cannot drift.
    const ray = pointerRay(pointerPx, context.camera, context.domElement);
    const last = recent[recent.length - 1];
    if (axisLock && axisLock !== 'lastSegment' && last) {
        const dir = AXIS_DIRS[axisLock];
        const fallback = projectOnAxis(planePoint, last, dir);
        const ride = ray ? closestAxisPointToRay(ray, last, dir, fallback) : fallback;

        // Exact geometry ON the locked line captures precisely — a
        // collinear endpoint must land exactly (crossing inference cannot
        // see it: its alignment line IS the locked line, hence parallel).
        const onLineTol = worldPerPx(context.camera, context.domElement, ride) * aperture;
        const u = dir.clone().normalize();
        const onLineHits: Array<{ point: THREE.Vector3; kind: InferenceKind; d: number }> = [];
        const considerOnLine = (p: THREE.Vector3, kind: InferenceKind): void => {
            const rel = p.clone().sub(last);
            if (rel.clone().addScaledVector(u, -rel.dot(u)).length() > onLineTol) return;
            const d = screenOf(p).distanceTo(pointerPx);
            if (d > aperture * CONVERGENCE_APERTURE_FACTOR) return;
            onLineHits.push({ point: p.clone(), kind, d });
        };
        pools.vertices.forEach((v) => considerOnLine(v, 'endpoint'));
        pools.edgeMidpoints.forEach((m) => considerOnLine(m, 'midpoint'));
        pools.centers.forEach((c) => considerOnLine(c, 'center'));
        onLineHits.sort((a, b) => a.d - b.d);
        const onLine = onLineHits[0] ?? null;

        // Alignment references crossing the lock: another line's endpoint
        // (its axis lines through it) or the edge itself (its line).
        // Engaged by POINTER PROXIMITY — the locked endpoint lives on the
        // lock, so the reference the user touches sits under the cursor
        // while the crossing can sit far along it: the point slides to the
        // crossing and the direction cannot drift. Nearest crossing wins;
        // near-parallel references never form a usable one.
        let crossing: { point: THREE.Vector3; guide: InferenceGuideLine; d: number } | null = null;
        if (ray) {
            const rect = context.domElement.getBoundingClientRect();
            for (const c of collectLockedCrossingCandidates(pools, context, pointerPx, aperture, recent)) {
                if (Math.abs(dir.dot(c.dir)) > 0.999) continue;
                const corner = intersectLines(last, dir, c.anchor, c.dir);
                if (!corner) continue; // skew in 3D
                const cp = screenOf(corner);
                // Sanity: the crossing must land inside the visible view.
                if (cp.x < -rect.width * 0.5 || cp.x > rect.width * 1.5) continue;
                if (cp.y < -rect.height * 0.5 || cp.y > rect.height * 1.5) continue;
                const d = cp.distanceTo(pointerPx);
                if (!crossing || d < crossing.d) crossing = { point: corner, guide: c.guide, d };
            }
        }

        const axisLine = axisSpan(last, dir, context.camera);
        if (onLine) {
            return {
                point: onLine.point,
                kind: onLine.kind,
                lockedAxis: axisLock,
                axis: axisLock,
                axisLine,
            };
        }
        if (crossing) {
            return {
                point: crossing.point,
                kind: 'onAxis',
                lockedAxis: axisLock,
                axis: axisLock,
                axisLine,
                guideLines: [crossing.guide],
            };
        }
        return {
            point: ride,
            kind: 'onAxis',
            lockedAxis: axisLock,
            axis: axisLock,
            axisLine,
        };
    }

    // 2) Priority ladder candidates within the aperture.
    const candidates: InferenceCandidate[] = [];
    const consider = (p: THREE.Vector3, kind: InferenceKind, priority: number): void => {
        const d = screenOf(p).distanceTo(pointerPx);
        if (d <= aperture) candidates.push({ point: p, kind, priority });
    };
    if (snapOn('endpoint')) pools.vertices.forEach((v) => consider(v, 'endpoint', 0));
    // Tape Measure guide POINTS snap like endpoints (blue marker).
    if (guideSnap) {
        for (const guide of Object.values(context.guides)) {
            if (guide.kind !== 'point') continue;
            consider(new THREE.Vector3(...guide.a), 'guide', 0.5);
        }
    }
    if (snapOn('midpoint')) pools.edgeMidpoints.forEach((m) => consider(m, 'midpoint', 1));
    // On-edge: nearest point of any edge within the aperture.
    let bestOnEdge: { point: THREE.Vector3; d: number; edge: { a: THREE.Vector3; b: THREE.Vector3 } } | null = null;
    if (snapOn('nearest')) {
        for (const edge of pools.edges) {
            const closest = closestPointOnSegment(pointerPx, screenOf(edge.a), screenOf(edge.b), edge.a, edge.b);
            if (closest) {
                const d = screenOf(closest).distanceTo(pointerPx);
                if (d <= aperture && (!bestOnEdge || d < bestOnEdge.d)) {
                    bestOnEdge = { point: closest, d, edge };
                }
            }
        }
    }
    if (bestOnEdge) candidates.push({ point: bestOnEdge.point, kind: 'onEdge', priority: 2 });
    if (snapOn('center')) pools.centers.forEach((c) => consider(c, 'center', 3));

    candidates.sort((a, b) => a.priority - b.priority || screenOf(a.point).distanceTo(pointerPx) - screenOf(b.point).distanceTo(pointerPx));

    if (candidates.length > 0) {
        return {
            point: candidates[0].point,
            kind: candidates[0].kind,
            lockedAxis: axisLock,
            edge: candidates[0].kind === 'onEdge' && bestOnEdge ? bestOnEdge.edge : undefined,
        };
    }

    // 3) Tape Measure guide lines: deliberate construction references. The
    // cursor within the aperture of a guide's screen projection rides that
    // infinite 3D line (resolved against the pointer ray, like an axis
    // grab); guide POINTS already joined the object-snap ladder above.
    if (guideSnap) {
        let bestGuide: { point: THREE.Vector3; d: number } | null = null;
        for (const guide of Object.values(context.guides)) {
            if (guide.kind !== 'line' || !guide.b) continue;
            const a = new THREE.Vector3(...guide.a);
            const b = new THREE.Vector3(...guide.b);
            if (a.distanceToSquared(b) < 1e-12) continue;
            const sa = screenOf(a);
            const dir = screenOf(b).sub(sa);
            if (dir.lengthSq() < 1e-9) continue; // edge-on: not inferable
            const rel = pointerPx.clone().sub(sa);
            const foot = sa.clone().addScaledVector(dir, rel.dot(dir) / dir.lengthSq());
            const d = foot.distanceTo(pointerPx);
            if (d > aperture) continue;
            if (!bestGuide || d < bestGuide.d) {
                const dirW = b.clone().sub(a).normalize();
                bestGuide = {
                    point: ray
                        ? closestAxisPointToRay(ray, a, dirW, planePoint)
                        : projectOnAxis(planePoint, a, dirW),
                    d,
                };
            }
        }
        if (bestGuide) {
            return { point: bestGuide.point, kind: 'guide', lockedAxis: axisLock };
        }
    }

    // 4) Live axis inference (Sketch direction grab): the cursor's screen
    // heading from the last point is matched against the world X/Y/Z axes
    // and the previous segment; the endpoint then rides that 3D axis line
    // via the pointer ray. Unlike a drawing-plane projection this makes
    // vertical strokes drawable off the ground plane: mouse up = line up.
    // Skipped while Alt is held (axisSnapSuppressed) — the orthogonal grab
    // is a convenience the user can mute mid-stroke; a manual arrow-key
    // lock (2) still wins, explicit direction beats suppression.
    if (last && ray && !axisSnapSuppressed) {
        const axisId = inferScreenAxis(
            context.camera, context.domElement, pointerPx, last, recent,
        );
        if (axisId) {
            const segDir = recent.length >= 2
                ? recent[recent.length - 1].clone().sub(recent[recent.length - 2])
                : null;
            const dir = axisId === 'lastSegment'
                ? (segDir && segDir.lengthSq() > 1e-12 ? segDir.normalize() : null)
                : AXIS_DIRS[axisId];
            if (dir) {
                const fallback = projectOnAxis(planePoint, last, dir);
                const projected = closestAxisPointToRay(ray, last, dir, fallback);
                return {
                    point: projected,
                    kind: 'onAxis',
                    lockedAxis: axisId === 'lastSegment' ? 'lastSegment' : axisLock,
                    axis: axisId,
                    axisLine: axisSpan(last, dir, context.camera),
                };
            }
        }
    }

    // 5) From-point alignment inference (Sketch linear inference): the
    // cursor's screen heading from ANY geometry point — not just the last
    // commit — is matched against the world axes, the previous segment and
    // edge extensions; the endpoint rides that 3D alignment line and the
    // classic dotted guide is drawn through the reference point. Two
    // matches on different axes CONVERGE to their intersection corner.
    // Alt mutes it together with the live orthogonal grab.
    if (ray && !axisSnapSuppressed) {
        const aligned = resolveFromPointAlignment({
            pools, context, pointerPx, ray, recent, planePoint, aperture,
        });
        if (aligned) {
            return {
                point: aligned.point,
                kind: 'guide',
                lockedAxis: axisLock,
                axis: aligned.axis,
                guideLines: aligned.guideLines,
            };
        }
    }

    // 6) Free point on the active plane (face / ground / guide).
    return { point: planePoint, kind: 'onFace', lockedAxis: axisLock };
}

// ===================== from-point alignment inference =====================

/** One from-point alignment candidate: the cursor's screen heading from
 *  `anchor` matches the 3D line `anchor + s*dir` within the grab angle. */
interface AlignmentCandidate {
    anchor: THREE.Vector3;
    dir: THREE.Vector3;
    /** Direction identity: a world axis, the previous segment, or an edge
     *  extension. Drives guide coloring; extensions never converge. */
    id: InferredAxis | 'extension';
    angle: number;
    /** The endpoint riding the alignment line (resolved against the ray). */
    resolved: THREE.Vector3;
    /** Dotted guide drawn through the reference geometry when engaged. */
    guide: InferenceGuideLine;
}

export interface FromPointAlignmentResult {
    point: THREE.Vector3;
    axis: InferredAxis | undefined;
    guideLines: InferenceGuideLine[];
}

/**
 * Collect the from-point alignment candidates for one pointer position
 * (pure). Anchors are the snap pools' points; a cheap heading pre-filter
 * against reference screen directions keeps the exact per-anchor sampling
 * to the few geometrically plausible candidates, so the pass stays linear
 * in the pool sizes like the object-snap ladder it extends. Shared by the
 * free alignment rung and the forced-lock crossing capture.
 */
function collectAlignmentCandidates(args: {
    pools: GeometryPools;
    context: InferenceContext;
    pointerPx: THREE.Vector2;
    ray: THREE.Ray;
    recent: THREE.Vector3[];
    planePoint: THREE.Vector3;
    aperture: number;
}): AlignmentCandidate[] {
    const { pools, context, pointerPx, ray, recent, planePoint, aperture } = args;
    const screenOf = (p: THREE.Vector3) => toScreen(p, context.camera, context.domElement);

    const last = recent[recent.length - 1];
    const segDir = recent.length >= 2
        ? recent[recent.length - 1].clone().sub(recent[recent.length - 2])
        : null;
    const dirCandidates: Array<{ id: InferredAxis; dir: THREE.Vector3 }> = [
        { id: 'x', dir: AXIS_DIRS.x },
        { id: 'y', dir: AXIS_DIRS.y },
        { id: 'z', dir: AXIS_DIRS.z },
    ];
    if (segDir && segDir.lengthSq() > 1e-12) {
        dirCandidates.push({ id: 'lastSegment', dir: segDir.normalize() });
    }
    // Reference screen directions for the cheap pre-filter, sampled once
    // near the stroke tip (perspective makes screen directions
    // position-dependent; the widened pre-filter tolerance absorbs the
    // drift between the reference point and far anchors).
    const refPoint = last ?? planePoint;
    const refDirs = dirCandidates
        .map(({ id, dir }) => ({ id, ds: screenDirAt(dir, refPoint, context) }))
        .filter((c): c is { id: InferredAxis; ds: THREE.Vector2 } => c.ds !== null);
    if (refDirs.length === 0) return [];
    const preCos = Math.cos(ALIGN_PREFILTER_RAD);

    const candidates: AlignmentCandidate[] = [];
    const corridor = aperture * ALIGN_CORRIDOR_APERTURE_FACTOR;
    /** Perpendicular screen distance from the pointer to the alignment
     *  line through `anchorScreen` with screen direction `ds`. */
    const perpPx = (anchorScreen: THREE.Vector2, ds: THREE.Vector2): number => {
        const rel = pointerPx.clone().sub(anchorScreen);
        return rel.clone().addScaledVector(ds, -rel.dot(ds)).length();
    };

    /** Point anchor (vertex / midpoint / center) vs the axis directions.
     *  The stroke tip is skipped — its own directions belong to the live
     *  axis grab that already ran. */
    const considerPoint = (anchor: THREE.Vector3): void => {
        if (last && anchor.distanceToSquared(last) < 1e-12) return;
        const s = screenOf(anchor);
        const heading = pointerPx.clone().sub(s);
        if (heading.length() < MIN_HEADING_PX) return;
        const h = heading.clone().normalize();
        if (!refDirs.some(({ ds }) => Math.abs(h.dot(ds)) >= preCos)) return;
        for (const { id, dir } of dirCandidates) {
            const ds = screenDirAt(dir, anchor, context);
            if (!ds) continue; // axis edge-on at this anchor
            const angle = Math.acos(THREE.MathUtils.clamp(Math.abs(h.dot(ds)), -1, 1));
            if (angle > SKETCH_AXIS_TOLERANCE_RAD) continue;
            if (perpPx(s, ds) > corridor) continue; // off the true line
            const span = axisSpan(anchor, dir, context.camera);
            candidates.push({
                anchor,
                dir,
                id,
                angle,
                resolved: closestAxisPointToRay(ray, anchor, dir, planePoint),
                guide: { a: span.a, b: span.b, axis: id },
            });
        }
    };

    /** Edge extension: the heading from the endpoint the grab is past,
     *  along the edge's own direction. Engages only beyond the segment
     *  ends — inside the span belongs to the on-edge rung. */
    const considerExtension = (edge: { a: THREE.Vector3; b: THREE.Vector3 }): void => {
        const dir = edge.b.clone().sub(edge.a);
        const len = dir.length();
        if (len < 1e-9) return;
        dir.divideScalar(len);
        // Pre-filter at the midpoint with the edge's own screen direction.
        const dsMid = screenDirAt(dir, edge.a.clone().addScaledVector(dir, len / 2), context);
        if (!dsMid) return;
        const sMid = screenOf(edge.a.clone().addScaledVector(dir, len / 2));
        const hMid = pointerPx.clone().sub(sMid);
        if (hMid.length() < MIN_HEADING_PX) return;
        if (Math.abs(hMid.normalize().dot(dsMid)) < preCos) return;
        // The grab point rides the infinite line through the edge; which
        // END anchors it falls out of the resolved position along it.
        const resolved = closestAxisPointToRay(ray, edge.a, dir, planePoint);
        const t = resolved.clone().sub(edge.a).dot(dir);
        const clearance = len * EXTENSION_CLEARANCE;
        if (t >= -clearance && t <= len + clearance) return; // inside the span
        const anchor = t < 0 ? edge.a : edge.b;
        const other = t < 0 ? edge.b : edge.a;
        const s = screenOf(anchor);
        const heading = pointerPx.clone().sub(s);
        if (heading.length() < MIN_HEADING_PX) return;
        const h = heading.clone().normalize();
        const ds = screenDirAt(dir, anchor, context);
        if (!ds) return;
        const angle = Math.acos(THREE.MathUtils.clamp(Math.abs(h.dot(ds)), -1, 1));
        if (angle > SKETCH_AXIS_TOLERANCE_RAD) return;
        if (perpPx(s, ds) > corridor) return; // off the true line
        // Outward guide: from the far endpoint through the anchor, past it
        // (the Sketch extension-line reading).
        const outward = anchor.clone().sub(other).normalize();
        const extLen = Math.max(1, context.camera.position.distanceTo(anchor) * 1.5);
        candidates.push({
            anchor,
            dir,
            id: 'extension',
            angle,
            resolved,
            guide: { a: other.clone(), b: anchor.clone().addScaledVector(outward, extLen) },
        });
    };

    for (const anchor of pools.vertices) considerPoint(anchor);
    for (const anchor of pools.edgeMidpoints) considerPoint(anchor);
    for (const anchor of pools.centers) considerPoint(anchor);
    for (const edge of pools.edges) considerExtension(edge);
    return candidates;
}

/**
 * Resolve the best from-point alignment for the current cursor (pure;
 * called from resolveInference after the object snaps and axis grabs
 * missed).
 */
function resolveFromPointAlignment(args: {
    pools: GeometryPools;
    context: InferenceContext;
    pointerPx: THREE.Vector2;
    ray: THREE.Ray;
    recent: THREE.Vector3[];
    planePoint: THREE.Vector3;
    aperture: number;
}): FromPointAlignmentResult | null {
    const { context, pointerPx, aperture } = args;
    const candidates = collectAlignmentCandidates(args);
    if (candidates.length === 0) return null;
    const screenOf = (p: THREE.Vector3) => toScreen(p, context.camera, context.domElement);

    // Best angular match wins; ties resolve to the ride point nearest the
    // cursor so adjacent anchors don't flip the grab.
    const distPx = (p: THREE.Vector3) => screenOf(p).distanceTo(pointerPx);
    candidates.sort((a, b) => a.angle - b.angle || distPx(a.resolved) - distPx(b.resolved));
    const top = candidates[0];

    // Convergence: the top match plus any other point-axis line (extensions
    // never converge) whose intersection corner sits within the convergence
    // radius of the cursor — the classic two-guide corner capture. Scanned
    // in angle order so the most engaged partner wins.
    if (top.id !== 'extension') {
        for (const second of candidates) {
            if (second.id === 'extension' || second.id === top.id) continue;
            const corner = intersectLines(top.anchor, top.dir, second.anchor, second.dir);
            if (!corner) continue;
            if (screenOf(corner).distanceTo(pointerPx) > aperture * CONVERGENCE_APERTURE_FACTOR) continue;
            return {
                point: corner,
                axis: top.id,
                guideLines: [
                    { a: top.guide.a, b: top.guide.b, axis: top.id },
                    { a: second.guide.a, b: second.guide.b, axis: second.id },
                ],
            };
        }
    }
    return {
        point: top.resolved,
        axis: top.id === 'extension' ? undefined : top.id,
        guideLines: [top.guide],
    };
}

/**
 * Reference lines for the LOCKED crossing capture. Unlike the free
 * alignment pass — which engages when the POINTER itself rides the
 * alignment line — these engage by pointer proximity: the locked endpoint
 * lives on the lock, so the reference the user touches (another line's
 * endpoint or its edge) sits under the cursor while the crossing with the
 * lock can sit far along it. Point anchors engage the world axes and the
 * previous segment (hovering the point engages ALL of them, since the
 * heading degenerates there); edges engage as full reference lines within
 * the corridor. The crossing scan decides which reference captures.
 */
function collectLockedCrossingCandidates(
    pools: GeometryPools,
    context: InferenceContext,
    pointerPx: THREE.Vector2,
    aperture: number,
    recent: THREE.Vector3[]
): AlignmentCandidate[] {
    const screenOf = (p: THREE.Vector3) => toScreen(p, context.camera, context.domElement);
    const corridor = aperture * ALIGN_CORRIDOR_APERTURE_FACTOR;
    const perpPx = (anchorScreen: THREE.Vector2, ds: THREE.Vector2): number => {
        const rel = pointerPx.clone().sub(anchorScreen);
        return rel.clone().addScaledVector(ds, -rel.dot(ds)).length();
    };

    const dirCandidates: Array<{ id: InferredAxis; dir: THREE.Vector3 }> = [
        { id: 'x', dir: AXIS_DIRS.x },
        { id: 'y', dir: AXIS_DIRS.y },
        { id: 'z', dir: AXIS_DIRS.z },
    ];
    if (recent.length >= 2) {
        const segDir = recent[recent.length - 1].clone().sub(recent[recent.length - 2]);
        if (segDir.lengthSq() > 1e-12) dirCandidates.push({ id: 'lastSegment', dir: segDir.normalize() });
    }

    const candidates: AlignmentCandidate[] = [];

    const considerPoint = (anchor: THREE.Vector3): void => {
        const s = screenOf(anchor);
        const hovering = s.distanceTo(pointerPx) <= aperture;
        const heading = pointerPx.clone().sub(s);
        if (!hovering && heading.length() < MIN_HEADING_PX) return;
        const h = heading.clone().normalize();
        for (const { id, dir } of dirCandidates) {
            if (!hovering) {
                const ds = screenDirAt(dir, anchor, context);
                if (!ds) continue; // axis edge-on at this anchor
                const angle = Math.acos(THREE.MathUtils.clamp(Math.abs(h.dot(ds)), -1, 1));
                if (angle > SKETCH_AXIS_TOLERANCE_RAD) continue;
                if (perpPx(s, ds) > corridor) continue;
            }
            const span = axisSpan(anchor, dir, context.camera);
            candidates.push({
                anchor,
                dir,
                id,
                angle: 0,
                resolved: anchor.clone(), // unused by the crossing scan
                guide: { a: span.a, b: span.b, axis: id },
            });
        }
    };

    const considerEdge = (edge: { a: THREE.Vector3; b: THREE.Vector3 }): void => {
        const dir = edge.b.clone().sub(edge.a);
        const len = dir.length();
        if (len < 1e-9) return;
        dir.divideScalar(len);
        const sa = screenOf(edge.a);
        const ds = screenOf(edge.b).sub(sa);
        if (ds.lengthSq() < 1e-9) return; // edge-on: not inferable
        if (perpPx(sa, ds.normalize()) > corridor) return; // not touching the edge's line
        const span = axisSpan(edge.a.clone().addScaledVector(dir, len / 2), dir, context.camera);
        candidates.push({
            anchor: edge.a,
            dir,
            id: 'extension',
            angle: 0,
            resolved: edge.a.clone(), // unused by the crossing scan
            guide: { a: span.a, b: span.b },
        });
    };

    pools.vertices.forEach(considerPoint);
    pools.edgeMidpoints.forEach(considerPoint);
    pools.centers.forEach(considerPoint);
    pools.edges.forEach(considerEdge);
    return candidates;
}

/** Screen direction of the 3D line `at + s*dir`, sampled a small
 *  camera-relative step along it. Null when the line runs (nearly) at the
 *  camera — not visually inferable. */
function screenDirAt(
    dir: THREE.Vector3,
    at: THREE.Vector3,
    context: InferenceContext
): THREE.Vector2 | null {
    const step = Math.max(1e-3, context.camera.position.distanceTo(at) * 0.05);
    const anchor = toScreen(at, context.camera, context.domElement);
    const tip = toScreen(at.clone().addScaledVector(dir, step), context.camera, context.domElement);
    const ds = tip.sub(anchor);
    if (ds.length() < 2) return null;
    return ds.normalize();
}

/** Intersection of two 3D lines (origin + direction), or null when the
 *  lines run parallel or skew (the coplanarity residual fails). */
function intersectLines(
    a1: THREE.Vector3,
    u1: THREE.Vector3,
    a2: THREE.Vector3,
    u2: THREE.Vector3
): THREE.Vector3 | null {
    const w = a2.clone().sub(a1);
    const u1u1 = u1.dot(u1);
    const u2u2 = u2.dot(u2);
    const u1u2 = u1.dot(u2);
    const det = u1u1 * u2u2 - u1u2 * u1u2;
    if (Math.abs(det) < 1e-10) return null; // parallel
    const w1 = w.dot(u1);
    const w2 = w.dot(u2);
    const s = (w1 * u2u2 - u1u2 * w2) / det;
    const t = (w1 * u1u2 - u1u1 * w2) / det;
    const p1 = a1.clone().addScaledVector(u1, s);
    const p2 = a2.clone().addScaledVector(u2, t);
    return p1.distanceToSquared(p2) < 1e-6 ? p1 : null; // skew
}

/** Closest world point on a segment given both endpoints' screen projections. */
function closestPointOnSegment(
    pointer: THREE.Vector2,
    aScreen: THREE.Vector2,
    bScreen: THREE.Vector2,
    aWorld: THREE.Vector3,
    bWorld: THREE.Vector3
): THREE.Vector3 | null {
    const ab = bScreen.clone().sub(aScreen);
    const len2 = ab.lengthSq();
    if (len2 < 1e-9) return null;
    const t = Math.max(0, Math.min(1, pointer.clone().sub(aScreen).dot(ab) / len2));
    return aWorld.clone().lerp(bWorld, t);
}

/** Project `p` onto the infinite axis through `origin` with direction `dir`. */
function projectOnAxis(p: THREE.Vector3, origin: THREE.Vector3, dir: THREE.Vector3): THREE.Vector3 {
    const d = dir.clone().normalize();
    return origin.clone().addScaledVector(d, p.clone().sub(origin).dot(d));
}

/** Parse a VCB (measurements box) value: plain number for v1 lengths. */
export function parseVcbValue(text: string): number | null {
    const trimmed = text.trim().replace(',', '.');
    if (!/^-?\d*\.?\d+$/.test(trimmed)) return null;
    const value = parseFloat(trimmed);
    return Number.isFinite(value) ? value : null;
}
